/**
 * Admin console (ARCHITECTURE §11.5): users, devices, presence, missions, security and history,
 * plus the real-time MFA approver. Every privileged action runs against the Phase 5 admin API and
 * re-enters sudo mode on demand. This device is a trusted admin device (§9.6).
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import QRCode from 'qrcode';
import { adminGet, adminPost, adminDelete, sudo, logout, type Role, type Session } from '../auth/api.js';

type Tab = 'presence' | 'users' | 'devices' | 'missions' | 'security' | 'history';

interface UserRow { userId: number; username: string; role: Role; status: string; createdAt: number }
interface DeviceRow { deviceId: string; user: string; role: Role; status: string; label: string | null; platform: string | null; screen: string | null; tz: string | null; userAgent: string | null; pairingCode: string | null; lastIp: string | null; firstSeenAt: number | null; approvedAt: number | null; lastSeenAt: number | null; activeUntil: number | null }
interface PresenceRow { deviceId: string; user: string; role: string; ip: string | null; geo: string | null; isp: string | null; heartbeatAgeMs: number | null; pending: boolean; pairingCode: string | null; view: string | null }
interface MfaReq { shortId: string; ip: string | null; geo: string | null; isp: string | null; expiresAt: number }

/** Absolute local date-time, short form (with a full-precision title on hover). */
function when(ms: number | null): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/** Relative "how long until" — e.g. "in 6d", "in 23h", "in 14m", or "expired". */
function until(ms: number | null): string {
  if (!ms) return '—';
  const d = ms - Date.now();
  if (d <= 0) return 'expired';
  const days = Math.floor(d / 86_400_000);
  if (days >= 1) return `in ${days}d`;
  const hours = Math.floor(d / 3_600_000);
  if (hours >= 1) return `in ${hours}h`;
  return `in ${Math.max(1, Math.floor(d / 60_000))}m`;
}

/** Relative "how long ago" for a last-seen timestamp. */
function ago(ms: number | null): string {
  if (!ms) return 'never';
  const d = Date.now() - ms;
  if (d < 60_000) return 'just now';
  const mins = Math.floor(d / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(d / 3_600_000);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(d / 86_400_000)}d ago`;
}

/** Run a privileged action; if the server demands sudo, prompt for the password and retry once. */
function useSudo(): (action: () => Promise<{ status: number }>) => Promise<{ status: number }> {
  return useCallback(async (action) => {
    let r = await action();
    if (r.status === 403) {
      const pw = window.prompt('Re-enter your password to continue (sudo mode):');
      if (pw && (await sudo(pw))) r = await action();
    }
    return r;
  }, []);
}

export function AdminConsole({ session }: { session: Session }): ReactNode {
  const [tab, setTab] = useState<Tab>('presence');
  const [mfa, setMfa] = useState<MfaReq | null>(null);
  const withSudo = useSudo();

  // real-time MFA prompts over a dedicated admin socket (§9.6)
  useEffect(() => {
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${proto}://${location.host}/api/v1/ws`);
    ws.onmessage = (e) => {
      try {
        const m = JSON.parse(e.data as string) as { type: string; id?: string; ip?: string; geo?: string; isp?: string; expiresAt?: number };
        if (m.type === 'mfa:request') setMfa({ shortId: m.id ?? '', ip: m.ip ?? null, geo: m.geo ?? null, isp: m.isp ?? null, expiresAt: m.expiresAt ?? 0 });
        else if (m.type === 'mfa:resolved') setMfa((cur) => (cur ? null : cur));
      } catch {
        // ignore non-JSON frames
      }
    };
    return () => ws.close();
  }, []);

  return (
    <div className="admin">
      <header className="admin-top">
        <span className="admin-brand">Admin</span>
        <nav className="admin-tabs">
          {(['presence', 'users', 'devices', 'missions', 'security', 'history'] as Tab[]).map((t) => (
            <button key={t} className={t === tab ? 'admin-tab active' : 'admin-tab'} onClick={() => setTab(t)} data-testid={`tab-${t}`}>
              {t}
            </button>
          ))}
        </nav>
        <span className="admin-who">{session.user}</span>
        <button className="admin-signout" onClick={() => void logout().then(() => location.reload())}>Sign out</button>
      </header>
      <main className="admin-body">
        {tab === 'presence' && <Presence />}
        {tab === 'users' && <Users withSudo={withSudo} />}
        {tab === 'devices' && <Devices withSudo={withSudo} />}
        {tab === 'missions' && <Missions />}
        {tab === 'security' && <Security withSudo={withSudo} />}
        {tab === 'history' && <History />}
      </main>
      {mfa && <MfaApprover req={mfa} withSudo={withSudo} onDone={() => setMfa(null)} />}
    </div>
  );
}

type WithSudo = (action: () => Promise<{ status: number }>) => Promise<{ status: number }>;

function usePoll<T>(loader: () => Promise<T | null>, ms: number): [T | null, () => void] {
  const [data, setData] = useState<T | null>(null);
  const load = useCallback(() => void loader().then((d) => setData(d)), [loader]);
  useEffect(() => {
    load();
    const id = window.setInterval(load, ms);
    return () => window.clearInterval(id);
  }, [load, ms]);
  return [data, load];
}

function Presence(): ReactNode {
  const [rows] = usePoll<PresenceRow[]>(() => adminGet<PresenceRow[]>('presence'), 3000);
  return (
    <table className="admin-table" data-testid="presence-table">
      <thead><tr><th>User</th><th>Role</th><th>State</th><th>IP</th><th>Location</th><th>ISP</th><th>Heartbeat</th></tr></thead>
      <tbody>
        {(rows ?? []).map((r) => (
          <tr key={r.deviceId}>
            <td>{r.user}</td>
            <td>{r.role}</td>
            <td>{r.pending ? <span className="pill warn">pending {r.pairingCode ? `· ${r.pairingCode}` : ''}</span> : r.heartbeatAgeMs === null ? <span className="pill">offline</span> : <span className="pill ok">online</span>}</td>
            <td className="mono">{r.ip ?? '—'}</td>
            <td>{r.geo ?? '—'}</td>
            <td>{r.isp ?? '—'}</td>
            <td>{r.heartbeatAgeMs === null ? '—' : `${Math.round(r.heartbeatAgeMs / 1000)}s`}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Users({ withSudo }: { withSudo: WithSudo }): ReactNode {
  const [users, reload] = usePoll<UserRow[]>(() => adminGet<UserRow[]>('users'), 15000);
  const [reveal, setReveal] = useState<{ user: string; password: string } | null>(null);
  const [qr, setQr] = useState<{ user: string; dataUrl: string } | null>(null);
  const [newUser, setNewUser] = useState<{ username: string; role: Role }>({ username: '', role: 'UAV' });

  const create = async (): Promise<void> => {
    const r = await withSudo(() => adminPost<{ password: string }>('users', newUser).then((x) => ({ status: x.status, body: x.body }))) as { status: number; body?: { password: string } | null };
    if (r.status === 200 && r.body) {
      setReveal({ user: newUser.username, password: r.body.password });
      setNewUser({ username: '', role: 'UAV' });
      reload();
    }
  };
  const revealPw = async (u: UserRow): Promise<void> => {
    const r = (await withSudo(() => adminGet<{ password: string }>(`users/${u.userId}/password`).then((b) => ({ status: b ? 200 : 403, body: b })))) as { status: number; body?: { password: string } | null };
    if (r.body) setReveal({ user: u.username, password: r.body.password });
  };
  const rotate = async (u: UserRow): Promise<void> => {
    const r = (await withSudo(() => adminPost<{ password: string }>(`users/${u.userId}/rotate-password`))) as { status: number; body?: { password: string } | null };
    if (r.body) setReveal({ user: u.username, password: r.body.password });
  };
  const del = async (u: UserRow): Promise<void> => {
    if (!window.confirm(`Disable ${u.username}? All their devices are revoked.`)) return;
    await withSudo(() => adminDelete(`users/${u.userId}`));
    reload();
  };
  const enrol = async (u: UserRow): Promise<void> => {
    const r = (await withSudo(() => adminPost<{ url: string }>(`users/${u.userId}/enroll-token`))) as { status: number; body?: { url: string } | null };
    if (r.body?.url) setQr({ user: u.username, dataUrl: await QRCode.toDataURL(r.body.url, { margin: 1, width: 240 }) });
  };

  return (
    <div className="admin-panel">
      <div className="admin-newuser">
        <input placeholder="new username" value={newUser.username} onChange={(e) => setNewUser({ ...newUser, username: e.target.value })} data-testid="new-username" />
        <select value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value as Role })}>
          {(['UAV', 'FDC', 'OBSERVER', 'ADMIN'] as Role[]).map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <button onClick={() => void create()} disabled={!newUser.username} data-testid="create-user">Create user</button>
      </div>
      <table className="admin-table">
        <thead><tr><th>User</th><th>Role</th><th>Status</th><th /></tr></thead>
        <tbody>
          {(users ?? []).map((u) => (
            <tr key={u.userId}>
              <td>{u.username}</td><td>{u.role}</td><td>{u.status}</td>
              <td className="admin-actions">
                <button onClick={() => void revealPw(u)}>Reveal</button>
                <button onClick={() => void rotate(u)}>Rotate</button>
                <button onClick={() => void enrol(u)}>QR</button>
                <button className="danger" onClick={() => void del(u)}>Disable</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {reveal && (
        <Modal onClose={() => setReveal(null)}>
          <h2>Password for {reveal.user}</h2>
          <p className="reveal-note">Shown once. Give it to the operator over a trusted channel.</p>
          <div className="mfa-code" data-testid="revealed-password">{reveal.password}</div>
        </Modal>
      )}
      {qr && (
        <Modal onClose={() => setQr(null)}>
          <h2>Enrol a device for {qr.user}</h2>
          <p className="reveal-note">Single-use, expires in 10 minutes.</p>
          <img className="qr" src={qr.dataUrl} alt="enrolment QR" width={240} height={240} />
        </Modal>
      )}
    </div>
  );
}

function Devices({ withSudo }: { withSudo: WithSudo }): ReactNode {
  const [devices, reload] = usePoll<DeviceRow[]>(() => adminGet<DeviceRow[]>('devices'), 5000);
  const [code, setCode] = useState<Record<string, string>>({});

  const approve = async (d: DeviceRow): Promise<void> => {
    const r = await withSudo(() => adminPost(`devices/${d.deviceId}/approve`, { pairingCode: code[d.deviceId] ?? '' }));
    if (r.status === 200) reload();
  };
  const deny = async (d: DeviceRow): Promise<void> => { await adminPost(`devices/${d.deviceId}/deny`); reload(); };
  const deauth = async (d: DeviceRow): Promise<void> => {
    if (!window.confirm(`Deauthorise ${d.user}'s device? It is cut off immediately.`)) return;
    await withSudo(() => adminPost(`devices/${d.deviceId}/deauth`));
    reload();
  };
  const rename = async (d: DeviceRow): Promise<void> => {
    const name = window.prompt('Device name:', d.label ?? '');
    if (name === null) return;
    await adminPost(`devices/${d.deviceId}/label`, { label: name });
    reload();
  };

  const activeLabel = (d: DeviceRow): string => (d.role === 'ADMIN' ? 'trusted' : 'session');
  return (
    <table className="admin-table" data-testid="devices-table">
      <thead><tr><th>User</th><th>Role</th><th>Status</th><th>Device</th><th>Enrolled</th><th>Approved</th><th>Last seen</th><th>Active until</th><th /></tr></thead>
      <tbody>
        {(devices ?? []).map((d) => (
          <tr key={d.deviceId}>
            <td>{d.user}</td><td>{d.role}</td>
            <td><span className={`pill ${d.status === 'APPROVED' ? 'ok' : d.status === 'PENDING' ? 'warn' : 'bad'}`}>{d.status}</span></td>
            <td className="device-cell" title={d.userAgent ?? ''}>
              <div className="device-name">
                {d.label ?? 'Unknown device'}
                <button className="device-edit" title="Rename device" onClick={() => void rename(d)} aria-label="Rename device">✎</button>
              </div>
              <div className="cell-sub">{[d.platform, d.screen, d.tz].filter(Boolean).join(' · ') || '—'}</div>
              <div className="cell-sub mono">id {d.deviceId.slice(0, 8)} · {d.lastIp ?? 'no ip'}</div>
            </td>
            <td title={when(d.firstSeenAt)}>{when(d.firstSeenAt)}</td>
            <td title={when(d.approvedAt)}>{when(d.approvedAt)}</td>
            <td title={when(d.lastSeenAt)}>{ago(d.lastSeenAt)}</td>
            <td title={when(d.activeUntil)}>{d.status === 'APPROVED' && d.activeUntil ? <span className="mono">{until(d.activeUntil)} <span className="cell-sub">({activeLabel(d)})</span></span> : '—'}</td>
            <td className="admin-actions">
              {d.status === 'PENDING' && (
                <>
                  <input className="code-input" placeholder="pairing" value={code[d.deviceId] ?? ''} onChange={(e) => setCode({ ...code, [d.deviceId]: e.target.value })} data-testid={`code-${d.deviceId}`} />
                  <button onClick={() => void approve(d)} data-testid={`approve-${d.deviceId}`}>Approve</button>
                  <button className="danger" onClick={() => void deny(d)}>Deny</button>
                </>
              )}
              {d.status === 'APPROVED' && <button className="danger" onClick={() => void deauth(d)}>Deauth</button>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

interface MissionRow { missionId: string; target: string; createdAt: number; disposition: string; state: string | null }

function Missions(): ReactNode {
  const [rows, reload] = usePoll<MissionRow[]>(() => fetch('/api/v1/missions', { credentials: 'same-origin', headers: { 'x-lw-client': 'web' } }).then((r) => (r.ok ? r.json() : null)), 5000);
  const forceEnd = async (m: MissionRow): Promise<void> => {
    const reason = window.prompt('Reason for FORCE END:');
    if (reason === null) return;
    await fetch(`/api/v1/missions/${m.missionId}/commands`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'x-lw-client': 'web' },
      credentials: 'same-origin',
      body: JSON.stringify({ cmdId: `force-${Date.now()}`, type: 'FORCE_END_MISSION', missionId: m.missionId, precondition: null, time: null, payload: { reason } }),
    });
    reload();
  };
  const active = (m: MissionRow): boolean => m.disposition === 'ACTIVE' || m.disposition === 'AWAITING_DISPOSITION';
  return (
    <table className="admin-table">
      <thead><tr><th>Mission</th><th>Target</th><th>State</th><th>Disposition</th><th /></tr></thead>
      <tbody>
        {(rows ?? []).map((m) => (
          <tr key={m.missionId}>
            <td className="mono">{m.missionId}</td><td>{m.target}</td><td>{m.state ?? '—'}</td><td>{m.disposition}</td>
            <td className="admin-actions">
              <a href={`/api/v1/missions/${m.missionId}/export`}>Log</a>
              <a href={`/api/v1/missions/${m.missionId}/export?variant=forensic`}>Forensic</a>
              {active(m) && <button className="danger" onClick={() => void forceEnd(m)}>Force end</button>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Security({ withSudo }: { withSudo: WithSudo }): ReactNode {
  const [enrol, setEnrol] = useState<{ uri: string; dataUrl: string } | null>(null);
  const [code, setCode] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [requests] = usePoll<{ shortId: string; status: string; ip: string | null; createdAt: number }[]>(() => adminGet('mfa/requests'), 5000);

  const startEnrol = async (): Promise<void> => {
    const r = (await withSudo(() => adminPost<{ base32: string; uri: string }>('security/totp/enroll'))) as { status: number; body?: { base32: string; uri: string } | null };
    if (r.body) setEnrol({ uri: r.body.uri, dataUrl: await QRCode.toDataURL(r.body.uri, { margin: 1, width: 200 }) });
  };
  const confirm = async (): Promise<void> => {
    const r = (await withSudo(() => adminPost<{ ok: boolean }>('security/totp/confirm', { code }))) as { status: number; body?: { ok: boolean } | null };
    setMsg(r.body?.ok ? 'Authenticator enrolled.' : 'Wrong code — try again.');
    if (r.body?.ok) setEnrol(null);
  };

  return (
    <div className="admin-panel">
      <section className="admin-section">
        <h2>Authenticator app (TOTP)</h2>
        {enrol ? (
          <div className="totp-enrol">
            <img className="qr" src={enrol.dataUrl} alt="TOTP QR" width={200} height={200} />
            <div className="mfa-totp-row">
              <input inputMode="numeric" placeholder="000000" value={code} onChange={(e) => setCode(e.target.value)} />
              <button onClick={() => void confirm()} disabled={code.length < 6}>Confirm</button>
            </div>
          </div>
        ) : (
          <button onClick={() => void startEnrol()}>Enrol / reset authenticator</button>
        )}
        {msg ? <p className="auth-hint">{msg}</p> : null}
      </section>
      <section className="admin-section">
        <h2>Recent MFA requests</h2>
        <table className="admin-table">
          <thead><tr><th>Request</th><th>Status</th><th>IP</th></tr></thead>
          <tbody>{(requests ?? []).map((r) => <tr key={r.shortId + r.createdAt}><td>{r.shortId}</td><td>{r.status}</td><td className="mono">{r.ip ?? '—'}</td></tr>)}</tbody>
        </table>
      </section>
    </div>
  );
}

function History(): ReactNode {
  const [logins] = usePoll<{ at: number; username_entered: string | null; outcome: string; ip: string | null; method: string }[]>(() => adminGet('history/logins'), 10000);
  return (
    <table className="admin-table">
      <thead><tr><th>When</th><th>User</th><th>Method</th><th>Outcome</th><th>IP</th></tr></thead>
      <tbody>
        {(logins ?? []).map((l, i) => (
          <tr key={i}><td>{new Date(l.at).toLocaleString()}</td><td>{l.username_entered ?? '—'}</td><td>{l.method}</td><td><span className={`pill ${l.outcome === 'success' ? 'ok' : 'bad'}`}>{l.outcome}</span></td><td className="mono">{l.ip ?? '—'}</td></tr>
        ))}
      </tbody>
    </table>
  );
}

function MfaApprover({ req, withSudo, onDone }: { req: MfaReq; withSudo: WithSudo; onDone: () => void }): ReactNode {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const approve = async (): Promise<void> => {
    const r = await withSudo(() => adminPost(`mfa/${req.shortId}/approve`, { code }));
    if (r.status === 200) onDone();
    else setError('Wrong code.');
  };
  const deny = async (): Promise<void> => { await adminPost(`mfa/${req.shortId}/deny`); onDone(); };
  return (
    <Modal onClose={onDone}>
      <h2>Approve admin sign-in</h2>
      <p className="reveal-note">A new admin device is signing in{req.geo ? ` from ${req.geo}` : ''}{req.ip ? ` (${req.ip})` : ''}. Type the 4-digit code shown on that device.</p>
      <div className="mfa-totp-row">
        <input inputMode="numeric" placeholder="0000" value={code} onChange={(e) => setCode(e.target.value)} data-testid="approver-code" autoFocus />
        <button onClick={() => void approve()} disabled={code.length < 4} data-testid="approver-approve">Approve</button>
        <button className="danger" onClick={() => void deny()}>Deny</button>
      </div>
      {error ? <p className="auth-error">{error}</p> : null}
    </Modal>
  );
}

function Modal({ children, onClose }: { children: ReactNode; onClose: () => void }): ReactNode {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        {children}
      </div>
    </div>
  );
}
